/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isBefore,
  isSameDay,
  isToday,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";
import HeaderFour from "../../components/Header/HeaderFour";
import Footer from "../../components/Footer";
import Seo from "../../components/Seo";
import config from "../../config";
import makeRequest from "../../makeRequest";
import { PatientAuthContext } from "../../contexts/PatientAuthContext";
import { patientApi } from "../../utils/patientApi";

const BASE = config.api.baseUrl;
const WEEK_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/* ─── inject styles once ─── */
const STYLE_ID = "dr-profile-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes drSpin { to { transform: rotate(360deg); } }
    .dr-spinner { width:36px;height:36px;border:3px solid #e2ecf9;border-top-color:#076cec;border-radius:50%;animation:drSpin .7s linear infinite;margin:0 auto; }
    @keyframes drFadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    .dr-fade { animation: drFadeIn .3s ease; }
    .dr-cal-day {
      aspect-ratio:1;border-radius:7px;border:none;background:transparent;
      font-size:12px;font-weight:600;color:#1e293b;cursor:pointer;
      display:flex;align-items:center;justify-content:center;transition:all .18s;position:relative;
    }
    .dr-cal-day:hover:not(:disabled) { background:#eef5ff;color:#076cec; }
    .dr-cal-day.today { color:#076cec;font-weight:800; }
    .dr-cal-day.today::after { content:'';position:absolute;bottom:2px;left:50%;transform:translateX(-50%);width:3px;height:3px;border-radius:50%;background:#076cec; }
    .dr-cal-day.selected { background:linear-gradient(135deg,#076cec,#0d3b6e);color:#fff!important;box-shadow:0 3px 10px rgba(7,108,236,0.3); }
    .dr-cal-day.selected::after { background:#fff; }
    .dr-cal-day:disabled { color:#cbd5e1;cursor:not-allowed; }
    .dr-slot { border:1.5px solid #e2ecf9;border-radius:8px;background:#f8fafc;color:#1e293b;font-size:12px;font-weight:600;padding:7px 11px;cursor:pointer;transition:all .18s;display:flex;align-items:center;gap:5px; }
    .dr-slot:hover { border-color:#076cec;color:#076cec;background:#eef5ff; }
    .dr-slot.sel { background:linear-gradient(135deg,#076cec,#0d3b6e);color:#fff;border-color:#076cec;box-shadow:0 3px 10px rgba(7,108,236,0.25); }
    .dr-input { width:100%;border:1.5px solid #e2ecf9;border-radius:9px;padding:9px 13px;font-size:13px;color:#1e293b;background:#f8fafc;outline:none;transition:border-color .2s,box-shadow .2s;box-sizing:border-box; }
    .dr-input:focus { border-color:#076cec;box-shadow:0 0 0 3px rgba(7,108,236,0.1);background:#fff; }
    .dr-input::placeholder { color:#94a3b8; }
    .dr-book-btn { width:100%;background:linear-gradient(135deg,#076cec 0%,#0d3b6e 100%);color:#fff;border:none;border-radius:11px;padding:13px;font-size:14px;font-weight:800;cursor:pointer;transition:opacity .2s,transform .2s;display:flex;align-items:center;justify-content:center;gap:8px; }
    .dr-book-btn:hover:not(:disabled) { opacity:.9;transform:translateY(-1px); }
    .dr-book-btn:disabled { opacity:.55;cursor:not-allowed;transform:none; }
    .dr-tab { padding:8px 18px;border-radius:8px;border:1.5px solid #e2ecf9;background:#f8fafc;font-size:12px;font-weight:700;cursor:pointer;transition:all .2s;color:#64748b; }
    .dr-tab.active { background:#076cec;color:#fff;border-color:#076cec; }
    @keyframes drBounce { 0%,100%{transform:scale(1)}50%{transform:scale(1.08)} }
    .dr-success-icon { animation:drBounce 1s ease 1; }
    @media(max-width:900px){
      .dr-profile-grid { grid-template-columns:1fr!important; }
      .dr-hero-grid { grid-template-columns:1fr!important; }
      .dr-hero-img { width:120px!important;height:120px!important; }
    }
  `;
  document.head.appendChild(s);
}

/* ─── helpers ─── */
const STAR = (filled) => <i className={filled ? "fa-solid fa-star" : "fa-regular fa-star"} style={{ color: "#f59e0b", marginRight: 2 }} />;

const parseSlotTime = (ts) => {
  try {
    const t = ts.split(" ")[1];
    if (!t) return ts;
    const [h, m] = t.split(":");
    const hr = parseInt(h, 10);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`;
  } catch {
    return ts;
  }
};

function useDoctorImage(doctor) {
  const [src, setSrc] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!doctor?.id || !doctor?.hasImage) return;
    let cancelled = false;
    fetch(`${BASE}/website/doctor/${doctor.id}/image`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob && !cancelled) {
          const url = URL.createObjectURL(blob);
          ref.current = url;
          setSrc(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (ref.current) URL.revokeObjectURL(ref.current);
    };
  }, [doctor?.id, doctor?.hasImage]);
  return src;
}

/* ════════════════════════════════════════════════════════
   INLINE BOOKING WIDGET
════════════════════════════════════════════════════════ */
function BookingWidget({ doctor }) {
  const { patient, login } = useContext(PatientAuthContext);

  const successCardRef = useRef(null);

  /* calendar */
  const today = startOfDay(new Date());
  const maxDate = addMonths(today, 3);
  const [month, setMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  /* slots */
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const slotsRef = useRef(null);

  /* patient details */
  const [patientTab, setPatientTab] = useState("existing"); // "existing" | "new"
  const [newFirst, setNewFirst] = useState("");
  const [newLast, setNewLast] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newCnic, setNewCnic] = useState("");
  const [newDob, setNewDob] = useState("");
  const [newGender, setNewGender] = useState("1"); // 1=Male,2=Female

  /* OTP flow for "existing" tab */
  const [mrno, setMrno] = useState("");
  const [otpStep, setOtpStep] = useState("mrno"); // "mrno" | "select_phone" | "otp_pending"
  const [lookupResult, setLookupResult] = useState(null); // { mrno, firstName, lastName, phoneNumbers }
  const [selectedPhone, setSelectedPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpInfo, setOtpInfo] = useState(null); // { maskedPhone, expiresInMinutes }
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  /* new-patient OTP state */
  const [newPatientStep, setNewPatientStep] = useState("form"); // "form" | "otp_pending"
  const [newTempMrno, setNewTempMrno] = useState("");
  const [newOtp, setNewOtp] = useState("");
  const [newOtpInfo, setNewOtpInfo] = useState(null);
  const [newRegLoading, setNewRegLoading] = useState(false);

  /* booking state */
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [success, setSuccess] = useState(null); // { doctorName, date, time }

  const skmId = doctor?.skmId;

  const fetchSlots = useCallback(
    async (date) => {
      if (!skmId) return;
      const ds = format(date, "yyyy-MM-dd");
      setSlotsLoading(true);
      setSlots([]);
      setSelectedSlot(null);
      try {
        const res = await makeRequest({
          route: `/taleeb/slots?doctorId=${skmId}&startDate=${encodeURIComponent(ds + " 00:00:00")}&endDate=${encodeURIComponent(ds + " 23:59:59")}`,
          method: "get",
        });
        setSlots(res?.data?.slots?.slots || []);
      } catch {
        setSlots([]);
      } finally {
        setSlotsLoading(false);
        setTimeout(() => slotsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
      }
    },
    [skmId],
  );

  const handleDateSelect = (date) => {
    if (isBefore(date, today)) return;
    setSelectedDate(date);
    setSelectedSlot(null);
    fetchSlots(date);
  };

  const handleBook = async () => {
    // Only used for logged-in patients; new patients use handleVerifyAndBookNew
    const resolvedMrno = patient?.mrno;
    if (!resolvedMrno) {
      setBookingError("No patient session found. Please refresh.");
      return;
    }
    setBookingError("");
    setBooking(true);
    try {
      await makeRequest({
        route: "/taleeb/appointment",
        method: "post",
        data: { mrno: resolvedMrno, srNo: selectedSlot.SR_NO },
      });
      setSuccess({
        doctorName: doctor.name,
        date: format(selectedDate, "EEEE, MMMM d, yyyy"),
        time: parseSlotTime(selectedSlot.TIME_SLOT),
        mrno: resolvedMrno,
      });
    } catch (e) {
      setBookingError(e?.message || "Booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  /* OTP handlers */
  const resetOtp = () => {
    setOtpStep("mrno");
    setLookupResult(null);
    setSelectedPhone("");
    setOtp("");
    setOtpInfo(null);
    setOtpError("");
  };

  const handleMrnoLookup = async () => {
    if (!mrno.trim()) {
      setOtpError("Please enter your MR number.");
      return;
    }
    setOtpError("");
    setOtpLoading(true);
    try {
      const data = await patientApi.lookup({ mrno: mrno.trim() });
      setLookupResult(data);
      if (data.phoneNumbers?.length === 1) {
        // single phone — request OTP immediately
        const phone = data.phoneNumbers[0].normalized;
        setSelectedPhone(phone);
        const res = await patientApi.requestOtp({ mrno: data.mrno, selectedPhone: phone });
        setOtpInfo({ maskedPhone: res.maskedPhone?.masked ?? res.maskedPhone, expiresInMinutes: res.expiresInMinutes });
        setOtpStep("otp_pending");
      } else {
        setOtpStep("select_phone");
      }
    } catch (e) {
      setOtpError(e?.message || "Patient not found. Please check your MR number.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSelectPhone = async (phone) => {
    setOtpError("");
    setOtpLoading(true);
    setSelectedPhone(phone);
    try {
      const res = await patientApi.requestOtp({ mrno: lookupResult.mrno, selectedPhone: phone });
      setOtpInfo({ maskedPhone: res.maskedPhone?.masked ?? res.maskedPhone, expiresInMinutes: res.expiresInMinutes });
      setOtpStep("otp_pending");
    } catch (e) {
      setOtpError(e?.message || "Failed to send OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyAndBook = async () => {
    if (!otp.trim()) {
      setOtpError("Please enter the OTP.");
      return;
    }
    setOtpError("");
    setBooking(true);
    try {
      const authData = await patientApi.verifyOtp({ mrno: lookupResult.mrno, otp: otp.trim() });
      // Keep the user logged in
      login(authData);
      // OTP verified — book appointment
      await makeRequest({
        route: "/taleeb/appointment",
        method: "post",
        data: { mrno: lookupResult.mrno, srNo: selectedSlot.SR_NO },
      });
      setSuccess({
        doctorName: doctor.name,
        date: format(selectedDate, "EEEE, MMMM d, yyyy"),
        time: parseSlotTime(selectedSlot.TIME_SLOT),
        mrno: lookupResult.mrno,
        patientName: authData?.patient?.firstName ? `${authData.patient.firstName} ${authData.patient.lastName || ""}`.trim() : null,
      });
    } catch (e) {
      setOtpError(e?.message || "Invalid OTP or booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  /* new-patient helpers */
  const resetNewPatient = () => {
    setNewPatientStep("form");
    setNewTempMrno("");
    setNewOtp("");
    setNewOtpInfo(null);
  };

  const handleNewPatientRegister = async () => {
    if (!newFirst.trim() || !newPhone.trim() || !newDob || !newGender) {
      setBookingError("First name, phone number, date of birth and gender are required.");
      return;
    }
    setBookingError("");
    setNewRegLoading(true);
    try {
      const res = await makeRequest({
        route: "/taleeb/temp-patient",
        method: "post",
        data: {
          firstName: newFirst.trim(),
          lastName: newLast.trim() || undefined,
          phoneNumber: newPhone.trim(),
          cnic: newCnic.trim() || undefined,
          dob: newDob,
          gender: newGender,
        },
      });
      const mrno = res?.data?.patient?.MRNO || res?.data?.patient?.mrno;
      if (!mrno) throw new Error("Could not register patient. Please try again.");
      setNewTempMrno(mrno);
      const normalizedPhone = "92" + newPhone.trim().replace(/\D/g, "").slice(-10);
      const otpRes = await patientApi.requestOtp({ mrno, selectedPhone: normalizedPhone });
      setNewOtpInfo({
        maskedPhone: otpRes.maskedPhone?.masked ?? otpRes.maskedPhone,
        expiresInMinutes: otpRes.expiresInMinutes,
      });
      setNewPatientStep("otp_pending");
    } catch (e) {
      setBookingError(e?.message || "Registration failed. Please try again.");
    } finally {
      setNewRegLoading(false);
    }
  };

  const handleVerifyAndBookNew = async () => {
    if (!newOtp.trim()) {
      setBookingError("Please enter the OTP.");
      return;
    }
    setBookingError("");
    setBooking(true);
    try {
      await patientApi.verifyOtp({ mrno: newTempMrno, otp: newOtp.trim() });
      await makeRequest({
        route: "/taleeb/appointment",
        method: "post",
        data: { mrno: newTempMrno, srNo: selectedSlot.SR_NO },
      });
      setSuccess({
        doctorName: doctor.name,
        date: format(selectedDate, "EEEE, MMMM d, yyyy"),
        time: parseSlotTime(selectedSlot.TIME_SLOT),
        mrno: newTempMrno,
        patientName: `${newFirst} ${newLast}`.trim() || null,
      });
    } catch (e) {
      setBookingError(e?.message || "Verification or booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  /* calendar grid */
  const calDays = useMemo(() => {
    const start = startOfMonth(month);
    const days = eachDayOfInterval({ start, end: endOfMonth(month) });
    return { days, pad: getDay(start) };
  }, [month]);

  const canPrevMonth = format(startOfMonth(month), "yyyy-MM") > format(startOfMonth(today), "yyyy-MM");
  const canNextMonth = format(startOfMonth(month), "yyyy-MM") < format(startOfMonth(maxDate), "yyyy-MM");

  /* ── Success screen ── */
  const downloadCard = () => {
    const canvas = document.createElement("canvas");
    const dpr = 2;
    canvas.width = 640 * dpr;
    canvas.height = 380 * dpr;
    canvas.style.width = "640px";
    canvas.style.height = "380px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Background
    const bg = ctx.createLinearGradient(0, 0, 640, 380);
    bg.addColorStop(0, "#f0f6ff");
    bg.addColorStop(1, "#e8f1fb");
    ctx.fillStyle = bg;
    ctx.roundRect(0, 0, 640, 380, 16);
    ctx.fill();

    // Header band
    const hdr = ctx.createLinearGradient(0, 0, 640, 0);
    hdr.addColorStop(0, "#0d3b6e");
    hdr.addColorStop(1, "#076cec");
    ctx.fillStyle = hdr;
    ctx.roundRect(0, 0, 640, 80, [16, 16, 0, 0]);
    ctx.fill();

    // Hospital name
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.font = "bold 12px sans-serif";
    ctx.fillText("MAROOF INTERNATIONAL HOSPITAL", 32, 28);

    // "Appointment Confirmed"
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 22px sans-serif";
    ctx.fillText("Appointment Confirmed", 32, 58);

    // Check circle
    ctx.beginPath();
    ctx.arc(600, 40, 22, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.2)";
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 20px sans-serif";
    ctx.fillText("✓", 591, 47);

    // Row helper
    const row = (label, value, y) => {
      ctx.fillStyle = "#64748b";
      ctx.font = "500 12px sans-serif";
      ctx.fillText(label.toUpperCase(), 48, y);
      ctx.fillStyle = "#0d3b6e";
      ctx.font = "bold 15px sans-serif";
      ctx.fillText(value, 48, y + 20);
      ctx.strokeStyle = "#e2ecf9";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(48, y + 32);
      ctx.lineTo(592, y + 32);
      ctx.stroke();
    };

    row("Doctor", success.doctorName, 106);
    row("Date", success.date, 166);
    row("Time", success.time, 226);
    if (success.mrno) row("MR Number", success.mrno, 286);

    // Footer
    ctx.fillStyle = "#94a3b8";
    ctx.font = "11px sans-serif";
    ctx.fillText("Please arrive 15 minutes early with your MR Card.", 48, 360);

    const link = document.createElement("a");
    link.download = `appointment-${success.mrno || "receipt"}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  if (success) {
    return (
      <div className="dr-fade" style={{ textAlign: "center", padding: "28px 16px" }}>
        <div
          ref={successCardRef}
          style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #e2ecf9",
            padding: "20px 18px",
            marginBottom: 16,
            textAlign: "left",
          }}
        >
          {/* Receipt header */}
          <div
            style={{
              background: "linear-gradient(135deg,#0d3b6e,#076cec)",
              borderRadius: 10,
              padding: "14px 16px",
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginBottom: 3,
                }}
              >
                Maroof International Hospital
              </div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: "1rem" }}>Appointment Confirmed</div>
            </div>
            <div
              className="dr-success-icon"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa-solid fa-circle-check" style={{ color: "#fff", fontSize: 22 }} />
            </div>
          </div>

          {/* Receipt rows */}
          {[
            { label: "Doctor", value: success.doctorName, icon: "fa-user-doctor" },
            { label: "Date", value: success.date, icon: "fa-calendar" },
            { label: "Time", value: success.time, icon: "fa-clock", accent: true },
            success.mrno ? { label: "MR Number", value: success.mrno, icon: "fa-id-card" } : null,
            success.patientName ? { label: "Patient", value: success.patientName, icon: "fa-user" } : null,
          ]
            .filter(Boolean)
            .map((r) => (
              <div
                key={r.label}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: "1px solid #f1f5f9" }}
              >
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 7,
                    background: r.accent ? "linear-gradient(135deg,#076cec22,#076cec11)" : "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <i className={`fa-regular ${r.icon}`} style={{ color: r.accent ? "#076cec" : "#64748b", fontSize: 12 }} />
                </div>
                <div>
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    {r.label}
                  </div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 700, color: r.accent ? "#076cec" : "#0d3b6e" }}>{r.value}</div>
                </div>
              </div>
            ))}

          <div style={{ marginTop: 12, fontSize: "0.75rem", color: "#94a3b8", textAlign: "center" }}>
            <i className="fa-regular fa-clock" style={{ marginRight: 5 }} />
            Please arrive 15 minutes early with your MR Card.
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <button
            onClick={downloadCard}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              background: "linear-gradient(135deg,#076cec,#0d3b6e)",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "11px",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            <i className="fa-solid fa-download" />
            Save Appointment Card
          </button>
          <button
            onClick={() => {
              setSuccess(null);
              setSelectedDate(null);
              setSlots([]);
              setSelectedSlot(null);
              resetOtp();
            }}
            style={{
              background: "none",
              border: "1.5px solid #e2ecf9",
              color: "#64748b",
              borderRadius: 10,
              padding: "10px",
              fontWeight: 700,
              fontSize: "0.82rem",
              cursor: "pointer",
            }}
          >
            Book Another Slot
          </button>
        </div>
      </div>
    );
  }

  const showPatientForm = !!selectedSlot;
  const isLoggedIn = !!patient;

  return (
    <div className="dr-fade">
      {/* ── Calendar ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <button
            disabled={!canPrevMonth}
            onClick={() => setMonth((m) => subMonths(m, 1))}
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              border: "1.5px solid #e2ecf9",
              background: "#f8fafc",
              color: "#076cec",
              cursor: canPrevMonth ? "pointer" : "not-allowed",
              opacity: canPrevMonth ? 1 : 0.35,
              fontSize: 13,
            }}
          >
            <i className="fa-solid fa-chevron-left" />
          </button>
          <span style={{ fontWeight: 800, fontSize: "0.9rem", color: "#0d3b6e" }}>{format(month, "MMMM yyyy")}</span>
          <button
            disabled={!canNextMonth}
            onClick={() => setMonth((m) => addMonths(m, 1))}
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              border: "1.5px solid #e2ecf9",
              background: "#f8fafc",
              color: "#076cec",
              cursor: canNextMonth ? "pointer" : "not-allowed",
              opacity: canNextMonth ? 1 : 0.35,
              fontSize: 13,
            }}
          >
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 4 }}>
          {WEEK_DAYS.map((d) => (
            <div
              key={d}
              style={{
                textAlign: "center",
                fontSize: "0.65rem",
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                padding: "3px 0",
              }}
            >
              {d}
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
          {Array.from({ length: calDays.pad }).map((_, i) => (
            <div key={`p${i}`} />
          ))}
          {calDays.days.map((day) => {
            const past = isBefore(day, today);
            const sel = selectedDate && isSameDay(day, selectedDate);
            return (
              <button
                key={day.toISOString()}
                className={`dr-cal-day${isToday(day) ? " today" : ""}${sel ? " selected" : ""}`}
                disabled={past}
                onClick={() => handleDateSelect(day)}
              >
                {format(day, "d")}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Slots ── */}
      <div ref={slotsRef}>
        {!selectedDate ? (
          <div style={{ textAlign: "center", padding: "14px 0", color: "#94a3b8", fontSize: "0.82rem" }}>
            <i className="fa-regular fa-calendar" style={{ fontSize: 22, display: "block", marginBottom: 6 }} />
            Select a date to see available slots
          </div>
        ) : slotsLoading ? (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div className="dr-spinner" />
            <p style={{ marginTop: 10, color: "#64748b", fontSize: "0.8rem" }}>Checking availability…</p>
          </div>
        ) : slots.length === 0 ? (
          <div style={{ textAlign: "center", padding: "14px 0", color: "#94a3b8", fontSize: "0.82rem" }}>
            <i className="fa-solid fa-calendar-xmark" style={{ fontSize: 22, display: "block", marginBottom: 6, color: "#f87171" }} />
            No slots for {format(selectedDate, "MMM d")} — pick another day
          </div>
        ) : (
          <div className="dr-fade">
            <div
              style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 8,
              }}
            >
              <i className="fa-regular fa-clock" style={{ marginRight: 6, color: "#076cec" }} />
              {format(selectedDate, "EEE, MMM d")} — {slots.length} slot{slots.length !== 1 ? "s" : ""}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 14 }}>
              {slots.map((slot) => (
                <button
                  key={slot.SR_NO}
                  className={`dr-slot${selectedSlot?.SR_NO === slot.SR_NO ? " sel" : ""}`}
                  onClick={() => setSelectedSlot(slot)}
                >
                  <i className="fa-regular fa-clock" style={{ fontSize: 10 }} />
                  {parseSlotTime(slot.TIME_SLOT)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Patient Details (appears after slot is selected) ── */}
      {showPatientForm && (
        <div className="dr-fade" style={{ borderTop: "1.5px solid #e2ecf9", paddingTop: 16, marginTop: 4 }}>
          {isLoggedIn ? (
            <div
              style={{
                background: "#f0f9ff",
                border: "1px solid #bae6fd",
                borderRadius: 9,
                padding: "11px 14px",
                fontSize: "0.82rem",
                color: "#0369a1",
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <i className="fa-solid fa-circle-check" style={{ color: "#22c55e", fontSize: 16 }} />
              <div>
                Booking as <strong>{patient.firstName || patient.name || patient.mrno}</strong>
                <div style={{ opacity: 0.8 }}>MR# {patient.mrno}</div>
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#374151", marginBottom: 10 }}>
                <i className="fa-regular fa-user" style={{ marginRight: 6, color: "#076cec" }} />
                Your Details
              </div>
              <div style={{ display: "flex", gap: 7, marginBottom: 12 }}>
                <button
                  className={`dr-tab${patientTab === "existing" ? " active" : ""}`}
                  onClick={() => {
                    setPatientTab("existing");
                    resetOtp();
                  }}
                >
                  Have MR #
                </button>
                <button
                  className={`dr-tab${patientTab === "new" ? " active" : ""}`}
                  onClick={() => {
                    setPatientTab("new");
                    resetNewPatient();
                  }}
                >
                  New Patient
                </button>
              </div>

              {patientTab === "existing" ? (
                <div style={{ marginBottom: 12 }}>
                  {/* ── Step: enter MR# ── */}
                  {otpStep === "mrno" && (
                    <>
                      <div style={{ display: "flex", gap: 7 }}>
                        <input
                          className="dr-input"
                          placeholder="Your MR Number (e.g. M0100000001234)"
                          value={mrno}
                          onChange={(e) => {
                            setMrno(e.target.value);
                            setOtpError("");
                          }}
                          onKeyDown={(e) => e.key === "Enter" && handleMrnoLookup()}
                          style={{ flex: 1 }}
                        />
                        <button
                          onClick={handleMrnoLookup}
                          disabled={otpLoading}
                          style={{
                            flexShrink: 0,
                            background: "#076cec",
                            color: "#fff",
                            border: "none",
                            borderRadius: 9,
                            padding: "9px 14px",
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            cursor: otpLoading ? "not-allowed" : "pointer",
                            opacity: otpLoading ? 0.6 : 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {otpLoading ? <i className="fa-solid fa-spinner fa-spin" /> : "Send OTP"}
                        </button>
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 5 }}>
                        An OTP will be sent to your registered phone number to verify your identity.
                      </div>
                    </>
                  )}

                  {/* ── Step: select phone ── */}
                  {otpStep === "select_phone" && lookupResult && (
                    <div className="dr-fade">
                      <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#0d3b6e", marginBottom: 8 }}>
                        <i className="fa-regular fa-user" style={{ marginRight: 6, color: "#076cec" }} />
                        Welcome, {lookupResult.firstName || "Patient"} — select a number to receive OTP:
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {(lookupResult.phoneNumbers || []).map((ph) => (
                          <button
                            key={ph.normalized}
                            onClick={() => handleSelectPhone(ph.normalized)}
                            disabled={otpLoading}
                            style={{
                              background: selectedPhone === ph.normalized ? "#076cec" : "#f0f6ff",
                              color: selectedPhone === ph.normalized ? "#fff" : "#0d3b6e",
                              border: "1.5px solid #076cec",
                              borderRadius: 9,
                              padding: "9px 14px",
                              fontSize: "0.82rem",
                              fontWeight: 700,
                              cursor: "pointer",
                              textAlign: "left",
                              opacity: otpLoading ? 0.6 : 1,
                            }}
                          >
                            <i className="fa-solid fa-mobile-screen" style={{ marginRight: 8, fontSize: 12 }} />
                            {ph.masked}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={resetOtp}
                        style={{
                          marginTop: 10,
                          background: "none",
                          border: "none",
                          color: "#94a3b8",
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          padding: 0,
                        }}
                      >
                        ← Use a different MR number
                      </button>
                    </div>
                  )}

                  {/* ── Step: enter OTP ── */}
                  {otpStep === "otp_pending" && otpInfo && (
                    <div className="dr-fade">
                      <div
                        style={{
                          background: "#f0f9ff",
                          border: "1px solid #bae6fd",
                          borderRadius: 8,
                          padding: "10px 13px",
                          fontSize: "0.8rem",
                          color: "#0369a1",
                          marginBottom: 10,
                        }}
                      >
                        <i className="fa-solid fa-message" style={{ marginRight: 7 }} />
                        OTP sent to <strong>{otpInfo.maskedPhone}</strong>
                        {otpInfo.expiresInMinutes ? ` · expires in ${otpInfo.expiresInMinutes} min` : ""}
                      </div>
                      <div style={{ display: "flex", gap: 7 }}>
                        <input
                          className="dr-input"
                          placeholder="Enter 6-digit OTP"
                          value={otp}
                          onChange={(e) => {
                            setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                            setOtpError("");
                          }}
                          onKeyDown={(e) => e.key === "Enter" && handleVerifyAndBook()}
                          maxLength={6}
                          inputMode="numeric"
                          style={{ flex: 1, letterSpacing: "0.2em", fontWeight: 700, fontSize: "1rem" }}
                          autoFocus
                        />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7, fontSize: "0.72rem" }}>
                        <button
                          onClick={() => handleSelectPhone(selectedPhone)}
                          disabled={otpLoading}
                          style={{ background: "none", border: "none", color: "#076cec", cursor: "pointer", padding: 0, fontWeight: 600 }}
                        >
                          {otpLoading ? <i className="fa-solid fa-spinner fa-spin" /> : "Resend OTP"}
                        </button>
                        <button
                          onClick={resetOtp}
                          style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: 0 }}
                        >
                          ← Change number
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: 12 }}>
                  {/* ── Step: fill in details ── */}
                  {newPatientStep === "form" && (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
                          <input
                            className="dr-input"
                            placeholder="First Name *"
                            value={newFirst}
                            onChange={(e) => setNewFirst(e.target.value)}
                          />
                          <input className="dr-input" placeholder="Last Name" value={newLast} onChange={(e) => setNewLast(e.target.value)} />
                        </div>
                        <input
                          className="dr-input"
                          placeholder="Phone Number *"
                          value={newPhone}
                          onChange={(e) => setNewPhone(e.target.value)}
                        />
                        <input
                          className="dr-input"
                          placeholder="CNIC (optional)"
                          value={newCnic}
                          onChange={(e) => setNewCnic(e.target.value)}
                        />
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
                          <input
                            className="dr-input"
                            type="date"
                            value={newDob}
                            onChange={(e) => setNewDob(e.target.value)}
                            title="Date of Birth"
                          />
                          <select className="dr-input" value={newGender} onChange={(e) => setNewGender(e.target.value)}>
                            <option value="1">Male</option>
                            <option value="2">Female</option>
                          </select>
                        </div>
                      </div>
                      <div
                        style={{
                          background: "linear-gradient(135deg,#f0f6ff,#e8f1fb)",
                          borderRadius: 10,
                          padding: "10px 14px",
                          marginBottom: 12,
                          fontSize: "0.8rem",
                          color: "#374151",
                        }}
                      >
                        <div style={{ fontWeight: 700, color: "#0d3b6e", marginBottom: 3 }}>{doctor.name}</div>
                        <div>
                          {format(selectedDate, "EEE, MMM d yyyy")} ·{" "}
                          <span style={{ color: "#076cec", fontWeight: 700 }}>{parseSlotTime(selectedSlot.TIME_SLOT)}</span>
                        </div>
                      </div>
                      <button className="dr-book-btn" onClick={handleNewPatientRegister} disabled={newRegLoading}>
                        {newRegLoading ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin" /> Registering…
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-user-plus" /> Register &amp; Get OTP
                          </>
                        )}
                      </button>
                    </>
                  )}

                  {/* ── Step: enter OTP ── */}
                  {newPatientStep === "otp_pending" && newOtpInfo && (
                    <div className="dr-fade">
                      <div
                        style={{
                          background: "#f0f9ff",
                          border: "1px solid #bae6fd",
                          borderRadius: 8,
                          padding: "10px 13px",
                          fontSize: "0.8rem",
                          color: "#0369a1",
                          marginBottom: 10,
                        }}
                      >
                        <i className="fa-solid fa-message" style={{ marginRight: 7 }} />
                        OTP sent to <strong>{newOtpInfo.maskedPhone}</strong>
                        {newOtpInfo.expiresInMinutes ? ` · expires in ${newOtpInfo.expiresInMinutes} min` : ""}
                      </div>
                      <div style={{ display: "flex", gap: 7, marginBottom: 7 }}>
                        <input
                          className="dr-input"
                          placeholder="Enter 6-digit OTP"
                          value={newOtp}
                          onChange={(e) => {
                            setNewOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                            setBookingError("");
                          }}
                          onKeyDown={(e) => e.key === "Enter" && handleVerifyAndBookNew()}
                          maxLength={6}
                          inputMode="numeric"
                          style={{ flex: 1, letterSpacing: "0.2em", fontWeight: 700, fontSize: "1rem" }}
                          autoFocus
                        />
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12, fontSize: "0.72rem" }}>
                        <button
                          onClick={resetNewPatient}
                          style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: 0 }}
                        >
                          ← Change details
                        </button>
                      </div>
                      <div
                        style={{
                          background: "linear-gradient(135deg,#f0f6ff,#e8f1fb)",
                          borderRadius: 10,
                          padding: "10px 14px",
                          marginBottom: 12,
                          fontSize: "0.8rem",
                          color: "#374151",
                        }}
                      >
                        <div style={{ fontWeight: 700, color: "#0d3b6e", marginBottom: 3 }}>{doctor.name}</div>
                        <div>
                          {format(selectedDate, "EEE, MMM d yyyy")} ·{" "}
                          <span style={{ color: "#076cec", fontWeight: 700 }}>{parseSlotTime(selectedSlot.TIME_SLOT)}</span>
                        </div>
                      </div>
                      <button className="dr-book-btn" onClick={handleVerifyAndBookNew} disabled={booking || !newOtp.trim()}>
                        {booking ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin" /> Verifying &amp; Booking…
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-shield-check" /> Verify OTP &amp; Confirm Booking
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {(bookingError || otpError) && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                padding: "9px 12px",
                fontSize: "0.8rem",
                color: "#dc2626",
                marginBottom: 12,
              }}
            >
              <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 7 }} />
              {otpError || bookingError}
            </div>
          )}

          {/* Confirm bar + button — shown only for logged-in patients.
              New patients use the step-based OTP flow above.
              Existing patients (MR# tab) use the "Verify & Book" button in the OTP step below. */}
          {isLoggedIn && (
            <>
              <div
                style={{
                  background: "linear-gradient(135deg,#f0f6ff,#e8f1fb)",
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 12,
                  fontSize: "0.8rem",
                  color: "#374151",
                }}
              >
                <div style={{ fontWeight: 700, color: "#0d3b6e", marginBottom: 3 }}>{doctor.name}</div>
                <div>
                  {format(selectedDate, "EEE, MMM d yyyy")} ·{" "}
                  <span style={{ color: "#076cec", fontWeight: 700 }}>{parseSlotTime(selectedSlot.TIME_SLOT)}</span>
                </div>
              </div>
              <button className="dr-book-btn" onClick={handleBook} disabled={booking}>
                {booking ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" /> Booking…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-calendar-check" /> Confirm Appointment
                  </>
                )}
              </button>
            </>
          )}

          {/* OTP path: Verify & Book button shown only when OTP entry is active */}
          {!isLoggedIn && patientTab === "existing" && otpStep === "otp_pending" && (
            <>
              <div
                style={{
                  background: "linear-gradient(135deg,#f0f6ff,#e8f1fb)",
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 12,
                  fontSize: "0.8rem",
                  color: "#374151",
                }}
              >
                <div style={{ fontWeight: 700, color: "#0d3b6e", marginBottom: 3 }}>{doctor.name}</div>
                <div>
                  {format(selectedDate, "EEE, MMM d yyyy")} ·{" "}
                  <span style={{ color: "#076cec", fontWeight: 700 }}>{parseSlotTime(selectedSlot.TIME_SLOT)}</span>
                </div>
              </div>
              <button className="dr-book-btn" onClick={handleVerifyAndBook} disabled={booking || !otp.trim()}>
                {booking ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" /> Verifying &amp; Booking…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-shield-check" /> Verify OTP &amp; Confirm Booking
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {!skmId && (
        <div style={{ textAlign: "center", padding: 16, color: "#94a3b8", fontSize: "0.82rem" }}>
          Online booking not available for this doctor.
          <br />
          <a href="tel:+92-51-111-644-911" style={{ color: "#076cec", fontWeight: 700 }}>
            Call +92-51-111-644-911
          </a>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   CARD SHELL
════════════════════════════════════════════════════════ */
const Card = ({ title, icon, children, accent }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 16,
      padding: 22,
      boxShadow: "0 4px 18px rgba(7,108,236,0.06)",
      border: `1px solid ${accent ? "rgba(7,108,236,0.12)" : "rgba(7,108,236,0.06)"}`,
    }}
  >
    <h3 style={{ fontSize: "1rem", fontWeight: 800, color: "#0d3b6e", margin: "0 0 14px", display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          background: "linear-gradient(135deg,#076cec22,#076cec11)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className={`fa-regular ${icon}`} style={{ color: "#076cec", fontSize: 13 }} />
      </span>
      {title}
    </h3>
    {children}
  </div>
);

/* ════════════════════════════════════════════════════════
   DOCTOR MEDIA SECTION – Blogs & Vlogs Swipers
════════════════════════════════════════════════════════ */
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([^&?/\s]{11})/);
  return m ? m[1] : null;
}

const BlogCard = ({ blog }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const revokeRef = useRef(null);

  useEffect(() => {
    if (!blog.hasImage) return;
    let cancelled = false;
    fetch(`${BASE}/website/doctor-blog/${blog.id}/image`)
      .then((r) => r.blob())
      .then((b) => {
        if (cancelled) return;
        const url = URL.createObjectURL(b);
        revokeRef.current = url;
        setImgSrc(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (revokeRef.current) URL.revokeObjectURL(revokeRef.current);
    };
  }, [blog.id, blog.hasImage]);

  const date = blog.createdAt
    ? new Date(blog.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(7,108,236,.08)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ height: 180, overflow: "hidden", background: "#e8f0fb", position: "relative" }}>
        {imgSrc ? (
          <img src={imgSrc} alt={blog.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="fa-regular fa-image" style={{ fontSize: 36, color: "#b0c8ef" }} />
          </div>
        )}
        {date && (
          <span
            style={{
              position: "absolute",
              bottom: 10,
              left: 12,
              background: "#076cec",
              color: "#fff",
              fontSize: "0.72rem",
              fontWeight: 700,
              borderRadius: 6,
              padding: "2px 10px",
            }}
          >
            {date}
          </span>
        )}
      </div>
      <div style={{ padding: "16px 18px 18px", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <h4 style={{ margin: 0, fontSize: "0.97rem", fontWeight: 700, color: "#0d3b6e", lineHeight: 1.4 }}>{blog.title}</h4>
        {blog.summary && (
          <p style={{ margin: 0, fontSize: "0.83rem", color: "#6b7280", lineHeight: 1.6, flex: 1 }}>
            {blog.summary.length > 120 ? `${blog.summary.slice(0, 120)}…` : blog.summary}
          </p>
        )}
      </div>
    </div>
  );
};

const VlogCard = ({ vlog }) => {
  const ytId = getYouTubeId(vlog.youtubeUrl);
  const thumb = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null;

  return (
    <a href={vlog.youtubeUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "block", height: "100%" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(7,108,236,.08)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ height: 180, overflow: "hidden", background: "#1a1a1a", position: "relative" }}>
          {thumb ? (
            <img src={thumb} alt={vlog.title} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.92 }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="fa-brands fa-youtube" style={{ fontSize: 44, color: "#e00" }} />
            </div>
          )}
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "rgba(255,255,255,.92)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 10px rgba(0,0,0,.3)",
              }}
            >
              <i className="fa-solid fa-play" style={{ color: "#e00", fontSize: 18, marginLeft: 3 }} />
            </div>
          </div>
        </div>
        <div style={{ padding: "14px 16px 16px", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
          <h4 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, color: "#0d3b6e", lineHeight: 1.4 }}>{vlog.title}</h4>
          {vlog.description && (
            <p style={{ margin: 0, fontSize: "0.82rem", color: "#6b7280", lineHeight: 1.55 }}>
              {vlog.description.length > 90 ? `${vlog.description.slice(0, 90)}…` : vlog.description}
            </p>
          )}
          <span
            style={{
              marginTop: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              color: "#e00",
              fontSize: "0.78rem",
              fontWeight: 700,
            }}
          >
            <i className="fa-brands fa-youtube" /> Watch on YouTube
          </span>
        </div>
      </div>
    </a>
  );
};

const swiperBreakpoints = {
  0: { slidesPerView: 1, spaceBetween: 14 },
  640: { slidesPerView: 2, spaceBetween: 18 },
  1024: { slidesPerView: 3, spaceBetween: 22 },
};

const MediaSectionTitle = ({ icon, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: "linear-gradient(135deg,#0d3b6e,#076cec)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <i className={`fa-regular ${icon}`} style={{ color: "#fff", fontSize: 16 }} />
    </div>
    <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 800, color: "#0d3b6e" }}>{children}</h3>
  </div>
);

const DoctorMediaSection = ({ blogs = [], vlogs = [] }) => {
  if (!blogs.length && !vlogs.length) return null;

  return (
    <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 40 }}>
      {blogs.length > 0 && (
        <section>
          <MediaSectionTitle icon="fa-newspaper">Doctor Blogs</MediaSectionTitle>
          <div style={{ position: "relative" }}>
            <Swiper modules={[Navigation]} navigation breakpoints={swiperBreakpoints} style={{ paddingBottom: 8 }}>
              {blogs.map((blog) => (
                <SwiperSlide key={blog.id} style={{ height: "auto" }}>
                  <BlogCard blog={blog} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      )}

      {vlogs.length > 0 && (
        <section>
          <MediaSectionTitle icon="fa-circle-play">Doctor Vlogs</MediaSectionTitle>
          <div style={{ position: "relative" }}>
            <Swiper modules={[Navigation]} navigation breakpoints={swiperBreakpoints} style={{ paddingBottom: 8 }}>
              {vlogs.map((vlog) => (
                <SwiperSlide key={vlog.id} style={{ height: "auto" }}>
                  <VlogCard vlog={vlog} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   DOCTOR PROFILE MAIN
════════════════════════════════════════════════════════ */
const DoctorProfileMain = ({ doctor }) => {
  const imgSrc = useDoctorImage(doctor);

  const avgRating = useMemo(() => {
    if (!doctor?.ratings?.length) return 0;
    const sum = doctor.ratings.reduce((a, r) => a + (Number(r.rating) || 0), 0);
    return sum / doctor.ratings.length;
  }, [doctor]);

  const businessHours = doctor?.businessHours || [];
  const groupedHours = useMemo(() => {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return days.map((d) => ({
      day: d,
      slots: businessHours.filter((h) => (h.day || h.dayOfWeek || "").toLowerCase().startsWith(d.toLowerCase())),
    }));
  }, [businessHours]);

  return (
    <div style={{ background: "#f7fafc", padding: "40px 0 80px" }}>
      <div className="container" style={{ maxWidth: 1200 }}>
        {/* ── Hero ── */}
        <div
          style={{
            background: "linear-gradient(135deg,#0d3b6e 0%,#076cec 100%)",
            color: "#fff",
            borderRadius: 20,
            padding: "28px 32px",
            marginBottom: 28,
            display: "grid",
            gridTemplateColumns: "160px 1fr auto",
            gap: 24,
            alignItems: "center",
            boxShadow: "0 14px 40px rgba(7,108,236,0.25)",
          }}
          className="dr-hero-grid"
        >
          <div
            className="dr-hero-img"
            style={{
              width: 160,
              height: 160,
              borderRadius: "50%",
              overflow: "hidden",
              background: "rgba(255,255,255,0.15)",
              border: "3px solid rgba(255,255,255,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {imgSrc ? (
              <img src={imgSrc} alt={doctor.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <i className="fa-solid fa-user-doctor" style={{ fontSize: 64, opacity: 0.7 }} />
            )}
          </div>

          <div>
            <div
              style={{
                fontSize: "0.75rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#cecece",
                opacity: 0.8,
                marginBottom: 4,
              }}
            >
              {doctor.websiteDepartment?.name || "Maroof International Hospital"}
            </div>
            <h1 style={{ fontSize: "1.9rem", fontWeight: 800, margin: "0 0 6px", color: "white" }}>{doctor.name}</h1>
            {doctor.designation && <div style={{ fontSize: "0.9rem", opacity: 0.9, marginBottom: 8 }}>{doctor.designation}</div>}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 8 }}>
              {(doctor.websiteSpeciality?.name || doctor.speciality) && (
                <span
                  style={{ background: "rgba(255,255,255,0.15)", padding: "4px 12px", borderRadius: 999, fontSize: "0.78rem", fontWeight: 600 }}
                >
                  {doctor.websiteSpeciality?.name || doctor.speciality}
                </span>
              )}
            </div>
            {doctor.educationTagLine && (
              <div style={{ fontSize: "0.82rem", opacity: 0.85 }}>
                <i className="fa-regular fa-graduation-cap" style={{ marginRight: 7 }} />
                {doctor.educationTagLine}
              </div>
            )}
            {doctor.ratings?.length > 0 && (
              <div style={{ marginTop: 8, fontSize: "0.82rem" }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n}>{STAR(n <= Math.round(avgRating))}</span>
                ))}
                <span style={{ marginLeft: 6, opacity: 0.9 }}>
                  {avgRating.toFixed(1)} ({doctor.ratings.length} review{doctor.ratings.length !== 1 ? "s" : ""})
                </span>
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 9, minWidth: 160 }}>
            <a
              href="tel:+92-51-111-644-911"
              style={{
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontWeight: 700,
                padding: "10px 18px",
                borderRadius: 999,
                textAlign: "center",
                textDecoration: "none",
                fontSize: "0.82rem",
                border: "1px solid rgba(255,255,255,0.4)",
              }}
            >
              <i className="fa-solid fa-phone" style={{ marginRight: 7 }} />
              Call Hospital
            </a>
            <a
              href="https://wa.me/925111644911"
              target="_blank"
              rel="noreferrer"
              style={{
                background: "#22c55e",
                color: "#fff",
                fontWeight: 700,
                padding: "10px 18px",
                borderRadius: 999,
                textAlign: "center",
                textDecoration: "none",
                fontSize: "0.82rem",
              }}
            >
              <i className="fa-brands fa-whatsapp" style={{ marginRight: 7 }} />
              WhatsApp
            </a>
          </div>
        </div>

        {/* ── Body grid: left 2fr | right 1fr ── */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }} className="dr-profile-grid">
          {/* Left column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {doctor.description && (
              <Card title="About" icon="fa-circle-info">
                <p style={{ margin: 0, lineHeight: 1.75, color: "#374151", fontSize: "0.93rem" }}>{doctor.description}</p>
              </Card>
            )}

            {doctor.overviews?.length > 0 && (
              <Card title="Overview" icon="fa-list-check">
                <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.85, color: "#374151", fontSize: "0.9rem" }}>
                  {doctor.overviews.map((o) => (
                    <li key={o.id}>{o.description}</li>
                  ))}
                </ul>
              </Card>
            )}

            {doctor.specializations?.length > 0 && (
              <Card title="Specializations" icon="fa-stethoscope">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {doctor.specializations.map((s) => (
                    <span
                      key={s.id}
                      style={{
                        background: "#dbeafe",
                        color: "#1d4ed8",
                        padding: "5px 13px",
                        borderRadius: 999,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}
                    >
                      <i className="fa-solid fa-check" style={{ marginRight: 6, fontSize: 10 }} />
                      {s.name}
                    </span>
                  ))}
                </div>
              </Card>
            )}

            {doctor.education?.length > 0 && (
              <Card title="Education & Qualifications" icon="fa-graduation-cap">
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {doctor.education.map((e) => (
                    <div key={e.id} style={{ borderLeft: "3px solid #076cec", paddingLeft: 14 }}>
                      <div style={{ fontWeight: 700, color: "#0d3b6e", fontSize: "0.9rem" }}>{e.degree || e.description}</div>
                      {e.degree && e.description && <div style={{ color: "#374151", fontSize: "0.85rem", marginTop: 2 }}>{e.description}</div>}
                      {e.institute && (
                        <div style={{ color: "#6b7280", fontSize: "0.82rem", marginTop: 2 }}>
                          <i className="fa-regular fa-building-columns" style={{ marginRight: 5 }} />
                          {e.institute}
                        </div>
                      )}
                      {e.year && (
                        <div style={{ color: "#9ca3af", fontSize: "0.76rem", marginTop: 2 }}>
                          <i className="fa-regular fa-calendar" style={{ marginRight: 5 }} />
                          {e.year}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {doctor.ratings?.length > 0 && (
              <Card title="Patient Reviews" icon="fa-comment-medical">
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {doctor.ratings.slice(0, 6).map((r) => (
                    <div key={r.id} style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 12 }}>
                      <div style={{ marginBottom: 4 }}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <span key={n}>{STAR(n <= (r.rating || 0))}</span>
                        ))}
                        <strong style={{ marginLeft: 8, color: "#0d3b6e", fontSize: "0.85rem" }}>Verified Patient</strong>
                      </div>
                      {r.comment && <p style={{ margin: 0, color: "#374151", fontSize: "0.88rem" }}>"{r.comment}"</p>}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Right column: booking widget + schedule */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {/* ── Booking widget ── */}
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 4px 24px rgba(7,108,236,0.10)",
                border: "1.5px solid rgba(7,108,236,0.14)",
                overflow: "hidden",
              }}
            >
              {/* widget header */}
              <div
                style={{
                  background: "linear-gradient(135deg,#076cec,#0d3b6e)",
                  padding: "16px 20px",
                  color: "#fff",
                }}
              >
                <div style={{ fontWeight: 800, fontSize: "1rem", marginBottom: 2 }}>
                  <i className="fa-regular fa-calendar-plus" style={{ marginRight: 9 }} />
                  Book an Appointment
                </div>
                <div style={{ fontSize: "0.77rem", opacity: 0.85 }}>Select a date &amp; time below — no login required</div>
              </div>
              <div style={{ padding: "18px 16px" }}>
                <BookingWidget doctor={doctor} />
              </div>
            </div>

            {/* ── Availability schedule ── */}
            <Card title="Weekly Schedule" icon="fa-clock">
              {businessHours.length === 0 ? (
                <p style={{ margin: 0, color: "#6b7280", fontSize: "0.85rem" }}>
                  Call{" "}
                  <a href="tel:+92-51-111-644-911" style={{ color: "#076cec" }}>
                    +92-51-111-644-911
                  </a>{" "}
                  for available slots.
                </p>
              ) : (
                <table style={{ width: "100%", fontSize: "0.82rem", borderCollapse: "collapse" }}>
                  <tbody>
                    {groupedHours.map((g) => (
                      <tr key={g.day} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "7px 0", fontWeight: 700, color: "#0d3b6e", width: 44 }}>{g.day}</td>
                        <td style={{ padding: "7px 0", color: "#374151" }}>
                          {g.slots.length === 0 ? (
                            <span style={{ color: "#9ca3af" }}>Closed</span>
                          ) : (
                            g.slots.map((s, i) => (
                              <div key={s.id || i}>
                                {s.openingTime || ""}
                                {s.openingTime && s.closingTime ? " – " : ""}
                                {s.closingTime || ""}
                              </div>
                            ))
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            {/* ── Other links ── */}
            <Link
              to="/team"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: "#f1f5f9",
                color: "#0d3b6e",
                padding: "12px",
                borderRadius: 12,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: "0.85rem",
                border: "1.5px solid #e2ecf9",
              }}
            >
              <i className="fa-regular fa-users" />
              All Doctors
            </Link>
          </div>
        </div>

        {/* ── Blogs & Vlogs ── */}
        <DoctorMediaSection blogs={doctor.blogs || []} vlogs={doctor.vlogs || []} />
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════
   PAGE ENTRY POINT
════════════════════════════════════════════════════════ */
const DoctorProfile = () => {
  const { id } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    makeRequest({ route: `/website/doctor/${id}/public`, method: "get" })
      .then(({ data }) => {
        if (!cancelled) setDoctor(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error || "Doctor not found");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <>
      <Seo
        title={doctor?.name || "Doctor Profile"}
        description={
          doctor
            ? `${doctor.name} — ${doctor.designation || ""} ${doctor.websiteSpeciality?.name || ""}. Book an appointment at Maroof International Hospital.`
            : "Doctor profile at Maroof International Hospital."
        }
        path={`/doctor/${id}`}
        type="profile"
        jsonLd={
          doctor && {
            "@context": "https://schema.org",
            "@type": "Physician",
            name: doctor.name,
            jobTitle: doctor.designation,
            medicalSpecialty: doctor.websiteSpeciality?.name || doctor.speciality,
            worksFor: { "@type": "Hospital", name: "Maroof International Hospital" },
            url: `https://maroof.com.pk/doctor/${id}`,
          }
        }
      />
      <HeaderFour />
      {loading ? (
        <div style={{ padding: "120px 20px", textAlign: "center", color: "#6b7280" }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 36, color: "#076cec" }} />
          <p style={{ marginTop: 16 }}>Loading doctor profile…</p>
        </div>
      ) : error || !doctor ? (
        <div style={{ padding: "120px 20px", textAlign: "center" }}>
          <h2 style={{ color: "#0d3b6e" }}>Doctor not found</h2>
          <p style={{ color: "#6b7280" }}>{error}</p>
          <Link to="/team" style={{ marginTop: 20, display: "inline-block", color: "#076cec" }}>
            ← Back to All Doctors
          </Link>
        </div>
      ) : (
        <DoctorProfileMain doctor={doctor} />
      )}
      <Footer />
    </>
  );
};

export default DoctorProfile;
